import { ChevronDownIcon } from "app/components/Icons";
import Textarea from "app/components/Icons/Textarea";
import Input from "app/components/Input";
import MemberProfileAvatar from "app/components/MemberProfileAvatar";
import { Skeleton } from "app/components/Skeleton";
import MemberSelection from "app/containers/Features/components/MemberSelection";
import { filterActiveTeamMembers } from "app/containers/Features/components/MemberSelection/helper";
import { selectProjectMemberList } from "app/containers/Features/components/MemberSelection/redux/selector";
import { IMemberList } from "app/containers/Features/components/MemberSelection/types";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectComponentFormData, selectFieldLoading } from "../redux/selector";
import { componentActions } from "../redux/slice";
import { IComponent } from "../types";

const ComponentForm: React.FC = () => {
  const dispatch = useDispatch();
  const form = useSelector(selectComponentFormData);
  const projectMembers = useSelector(selectProjectMemberList);
  const isFieldLoading = useSelector(selectFieldLoading);
  const activeMembers = useMemo(
    () => filterActiveTeamMembers(projectMembers || []),
    [projectMembers],
  );

  const handleFormChange = (key: keyof IComponent, value: string) => {
    dispatch(componentActions.updateFormValue({ key, value }));
  };

  return (
     <div className="flex flex-col px-[24px] py-4 pt-[12px] gap-4">

      {/* 🔹 Name */}
      <div className="flex flex-col gap-y-4">
        {isFieldLoading ? (
          <Skeleton className="h-[48px] w-full" />
        ) : (
          <Input
            label="Component name"
            placeholder="Enter component name"
            id="component-name"
            inputClassName="!h-[48px]"
            value={form?.name}
            onChange={(e) => handleFormChange("name", e.target.value)}
          />
        )}

        {/* 🔹 Component Lead */}
        <div className="flex flex-col w-full">
          <label className="text-xs text-secondary mb-1">
            Component lead
          </label>

          {isFieldLoading ? (
            <Skeleton className="h-[44px] w-full" />
          ) : (
            <MemberSelection
              trigger={
                <button className="flex items-center justify-between w-full h-[44px] px-3 border rounded">
                  {form.lead ? (
                    <MemberProfileAvatar id={form.lead} />
                  ) : (
                    <span className="text-sm text-gray-300">
                      Choose component lead
                    </span>
                  )}
                  <ChevronDownIcon size={20} />
                </button>
              }
              selectedValue={form.lead}
              updateValue={(member: IMemberList) =>
                handleFormChange("lead", member?._id || "")
              }
              options={activeMembers}
              isFullWidth
            />
          )}
        </div>

        {/* 🔹 Default Assignee */}
        <div className="flex flex-col w-full">
          <label className="text-xs text-secondary mb-1">
            Default assignee
          </label>

          {isFieldLoading ? (
            <Skeleton className="h-[44px] w-full" />
          ) : (
            <MemberSelection
              trigger={
                <button className="flex items-center justify-between w-full h-[44px] px-3 border rounded">
                  {form.defaultAssignee ? (
                    <MemberProfileAvatar id={form.defaultAssignee} />
                  ) : (
                    <span className="text-sm text-gray-300">
                      Choose assignee
                    </span>
                  )}
                  <ChevronDownIcon size={20} />
                </button>
              }
              selectedValue={form.defaultAssignee}
              updateValue={(member: IMemberList) =>
                handleFormChange("defaultAssignee", member?._id || "")
              }
              options={activeMembers}
              isFullWidth
            />
          )}
        </div>

        {/* 🔹 Description */}
        {isFieldLoading ? (
          <Skeleton className="h-[144px] w-full" />
        ) : (
          <Textarea
            label="Component description"
            placeholder="Enter component description"
            id="component-description"
            value={form?.description}
            onChange={(e) =>
              handleFormChange("description", e.target.value)
            }
            disableAutoGrow
            inputClassName="!h-[144px] !min-h-[144px] !max-h-[144px]"
            className="!h-full min-h-0"
          />
        )}
      </div>
    </div>
  );
};

export default ComponentForm;
